import { PaymentDirection } from '@cpce/database/enums';
import type {
  EmployeeBankDetailsData,
  EmployeeWithBeneficiaries,
  SelectableBeneficiaryRow,
  SendDebtRemindersData,
  SendDebtRemindersResult,
  SetTiersAccountInput,
  TiersAccountRelink,
} from '@cpce/shared';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { apiPost } from '@/api/api.utils';
import { commiteeMutation, MutationMethod } from '@/api/mutations.utils';
import { queryClient } from '@/api/queries.utils';
import { CommiteeIdResolutionStatus, useCommiteeIdResolutionFromUrl } from '@/utils/router';
import { financialAccountsQueryKeys } from '../financial-accounts/financial-accounts.query-keys';
import { paymentsQueryKeys } from '../payments/payments.query-keys';
import { prestationsQueryKeys } from '../prestations/prestations.query-keys';
import { ECRITURES_REWRITE_INVALIDATIONS } from '../transactions/transactions.query-keys';
import { employeesQueryKeys } from './employees.query-keys';
import type { UpdateEmployeeData } from './employees.types';

function plural(count: number) {
  return count > 1 ? 's' : '';
}

/**
 * One mail per salarié, not per debt line, so the counts reported here are salariés. Anything
 * skipped is named rather than folded into the success count: a CSE that believes a relance went
 * out and never checks again is worse than a noisy toast.
 */
function reportDebtReminders(result: SendDebtRemindersResult) {
  const { sent, skippedWithoutEmail, skippedWithoutDebt } = result;

  if (sent > 0) {
    toast.success(`${sent} relance${plural(sent)} envoyée${plural(sent)}`);
  }
  if (skippedWithoutEmail > 0) {
    toast.error(
      `${skippedWithoutEmail} salarié${plural(skippedWithoutEmail)} sans adresse e-mail utilisable : aucune relance envoyée`,
      { duration: 10000 }
    );
  }
  if (skippedWithoutDebt > 0) {
    toast.error(
      `${skippedWithoutDebt} salarié${plural(skippedWithoutDebt)} sans dette sur les lignes sélectionnées`,
      { duration: 10000 }
    );
  }
  if (sent === 0 && skippedWithoutEmail === 0 && skippedWithoutDebt === 0) {
    toast.error('Aucune relance envoyée');
  }
}

export function useSendDebtReminders() {
  const commiteeIdResolution = useCommiteeIdResolutionFromUrl();

  const mutation = useMutation({
    mutationFn: async (data: SendDebtRemindersData) => {
      if (commiteeIdResolution.status !== CommiteeIdResolutionStatus.READY) {
        throw new Error('Commitee ID is not available yet');
      }
      return apiPost<SendDebtRemindersResult>(
        `/commitee/${commiteeIdResolution.commiteeId}/employees/send-debt-reminders`,
        data
      );
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: employeesQueryKeys.all });
      await queryClient.invalidateQueries({ queryKey: prestationsQueryKeys.all });
    },
    onSuccess: (result) => reportDebtReminders(result),
    onError: (error) => {
      toast.error(`L'opération a échoué: ${error instanceof Error ? error.message : 'Erreur'}`);
    },
  });

  return {
    sendDebtReminders: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
}

export function useUpdateEmployee(id: number) {
  return commiteeMutation({
    url: `/commitee/:commiteeId/employees/${id}`,
    method: MutationMethod.PATCH,
    invalidateQueries: [employeesQueryKeys.all],
    body: (data: UpdateEmployeeData) => data,
    successMessage: 'Salarié mis à jour',
  });
}

// The salarié's own cache entry, not the orders query nested under the same key.
function patchEmployee(id: number, patch: Partial<EmployeeWithBeneficiaries>) {
  const queryKey = employeesQueryKeys.one(id);
  queryClient.setQueriesData<EmployeeWithBeneficiaries>(
    { queryKey, predicate: (query) => query.queryKey.length === queryKey.length + 1 },
    (employee) => employee && { ...employee, ...patch }
  );
}

// Kept on our side: the billetterie card has no bank details. The remise de virements reads them
// for the SEPA file, so its pending list is refreshed too.
export function useSetEmployeeBankDetails(id: number) {
  return commiteeMutation<
    Pick<EmployeeWithBeneficiaries, 'iban' | 'bic'>,
    EmployeeBankDetailsData,
    EmployeeBankDetailsData
  >({
    url: `/commitee/:commiteeId/employees/${id}/bank-details`,
    method: MutationMethod.PUT,
    invalidateQueries: [paymentsQueryKeys.pending(PaymentDirection.outflow)],
    body: (data) => data,
    onSuccess: (bankDetails) => patchEmployee(id, bankDetails),
    successMessage: 'Coordonnées bancaires mises à jour',
  });
}

// Only the salarié's rows change in the wizard's whole-CSE list: patched in place rather than
// refetched (megabytes plus a billetterie call).
export function useSetEmployeeFinancialAccount(id: number) {
  return commiteeMutation<TiersAccountRelink, SetTiersAccountInput, SetTiersAccountInput>({
    url: `/commitee/:commiteeId/employees/${id}/financial-account`,
    method: MutationMethod.PATCH,
    invalidateQueries: [
      employeesQueryKeys.one(id),
      employeesQueryKeys.financialAccount(id),
      financialAccountsQueryKeys.all,
    ],
    body: (data) => data,
    onSuccess: async ({ account, movedEntriesCount }) => {
      queryClient.setQueriesData<SelectableBeneficiaryRow[]>(
        { queryKey: employeesQueryKeys.selectable },
        (rows) =>
          rows?.map((row) =>
            (row.type === 'employee' ? row.id : row.linkedEmployeeId) === id
              ? {
                  ...row,
                  financialAccountId: account?.id ?? null,
                  financialAccountCode: account?.code ?? null,
                }
              : row
          )
      );
      if (movedEntriesCount > 0) {
        await Promise.all(
          [prestationsQueryKeys.all, ...ECRITURES_REWRITE_INVALIDATIONS].map((queryKey) =>
            queryClient.invalidateQueries({ queryKey })
          )
        );
      }
    },
    successMessage: 'Compte client mis à jour',
  });
}
